import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Box,
  IconButton,
} from "@material-ui/core";
import { CloudUpload as UploadIcon } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import { LoadingButton } from "@material-ui/lab";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import DialogModal from "src/components/shared/DialogModal";
import { useSnackbar } from "src/components/snackbar";
import {
  useCrearCertificadoMutation,
  useActualizarCertificadoMutation,
} from "src/services/certificadosDigitalesApi";

const validationSchema = Yup.object({
  titular: Yup.string().nullable(),
  tipo: Yup.string().required("El tipo es requerido"),
  fecha_emision: Yup.date()
    .required("La fecha de emisión es requerida")
    .max(
      Yup.ref("fecha_vencimiento"),
      "La fecha de emisión no puede ser mayor a la fecha de vencimiento",
    ),
  fecha_vencimiento: Yup.date().required(
    "La fecha de vencimiento es requerida",
  ),
  estado: Yup.string().required("El estado es requerido"),
  observaciones: Yup.string().nullable(),
});

export default function CertificadoDigitalModal({
  open,
  onClose,
  cliente,
  certificado = null,
  sucursal,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [archivo, setArchivo] = useState(null);
  const [anexos, setAnexos] = useState([]);

  const [crearCertificado, { isLoading: creando }] =
    useCrearCertificadoMutation();
  const [actualizarCertificado, { isLoading: actualizando }] =
    useActualizarCertificadoMutation();

  const isEdit = Boolean(certificado);

  useEffect(() => {
    if (open) {
      setArchivo(null);
      setAnexos([]);
    }
  }, [open, certificado]);

  const initialValues = {
    titular: certificado?.titular || "",
    tipo: certificado?.tipo || "OTRO",
    fecha_emision: certificado?.fecha_emision
      ? String(certificado.fecha_emision).substring(0, 10)
      : "",
    fecha_vencimiento: certificado?.fecha_vencimiento
      ? String(certificado.fecha_vencimiento).substring(0, 10)
      : "",
    estado: certificado?.estado || "ACTIVO",
    observaciones: certificado?.observaciones || "",
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setArchivo(e.target.files[0]);
    }
  };

  const handleAnexosChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAnexos((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveAnexo = (indexToRemove) => {
    setAnexos((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSave = async (values, helpers = {}) => {
    const setSubmitting = helpers?.setSubmitting;
    const resetForm = helpers?.resetForm;
    try {
      const formData = new FormData();
      formData.append("sucursal", sucursal || "");
      formData.append("titular", values.titular || "");
      formData.append("tipo", values.tipo);
      formData.append("fecha_emision", values.fecha_emision);
      formData.append("fecha_vencimiento", values.fecha_vencimiento);
      if (isEdit) {
        formData.append("estado", values.estado);
      }
      formData.append("observaciones", values.observaciones || "");

      if (archivo) {
        formData.append("archivo", archivo);
      }

      if (anexos && anexos.length > 0) {
        anexos.forEach((anexo) => {
          formData.append("anexos", anexo);
        });
      }

      if (isEdit) {
        const response = await actualizarCertificado({
          id: certificado.id,
          formData,
        }).unwrap();
        enqueueSnackbar(
          response.message || "Certificado digital actualizado correctamente",
          { variant: response.tareaCreada === false ? "warning" : "success" },
        );
      } else {
        formData.append("cliente_id", cliente?.rut);
        const response = await crearCertificado(formData).unwrap();
        enqueueSnackbar(
          response.message || "Certificado digital creado correctamente",
          { variant: response.tareaCreada === false ? "warning" : "success" },
        );
      }

      if (resetForm) resetForm();
      onClose();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Error al guardar certificado",
        { variant: "error" },
      );
    } finally {
      if (setSubmitting) setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSave}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        handleSubmit: handleFormikSubmit,
      }) => (
        <Form onSubmit={handleFormikSubmit}>
          <DialogModal
            open={open}
            onClose={onClose}
            title={
              isEdit
                ? `Editar Certificado Digital - ${cliente?.nombre || ""}`
                : `Nuevo Certificado Digital - ${cliente?.nombre || ""}`
            }
            maxWidth="md"
            actions={
              <>
                <Button
                  onClick={onClose}
                  disabled={isSubmitting || creando || actualizando}
                >
                  Cancelar
                </Button>
                <LoadingButton
                  type="submit"
                  color="error"
                  variant="contained"
                  loading={isSubmitting || creando || actualizando}
                  onClick={handleFormikSubmit}
                >
                  {isEdit ? "Guardar Cambios" : "Crear Certificado"}
                </LoadingButton>
              </>
            }
          >
            <Grid container spacing={2} style={{ paddingTop: 8 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="titular"
                  label="Titular"
                  value={values.titular}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.titular && Boolean(errors.titular)}
                  helperText={touched.titular && errors.titular}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  name="tipo"
                  label="Tipo de Certificado *"
                  value={values.tipo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.tipo && Boolean(errors.tipo)}
                  helperText={touched.tipo && errors.tipo}
                  disabled={isSubmitting}
                >
                  <MenuItem value="SII">SII</MenuItem>
                  <MenuItem value="FEA">
                    Firma Electrónica Avanzada (FEA)
                  </MenuItem>
                  <MenuItem value="FES">
                    Firma Electrónica Simple (FES)
                  </MenuItem>
                  <MenuItem value="OTRO">Otro</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="fecha_emision"
                  label="Fecha de Emisión *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.fecha_emision}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.fecha_emision && Boolean(errors.fecha_emision)}
                  helperText={touched.fecha_emision && errors.fecha_emision}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="fecha_vencimiento"
                  label="Fecha de Vencimiento *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.fecha_vencimiento}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.fecha_vencimiento &&
                    Boolean(errors.fecha_vencimiento)
                  }
                  helperText={
                    touched.fecha_vencimiento && errors.fecha_vencimiento
                  }
                  disabled={isSubmitting}
                />
              </Grid>

              {isEdit && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    name="estado"
                    label="Estado *"
                    value={values.estado}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.estado && Boolean(errors.estado)}
                    helperText={touched.estado && errors.estado}
                    disabled={isSubmitting}
                  >
                    <MenuItem value="ACTIVO">Activo</MenuItem>
                    <MenuItem value="REVOCADO">Revocado</MenuItem>
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="observaciones"
                  label="Observaciones"
                  value={values.observaciones}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.observaciones && Boolean(errors.observaciones)}
                  helperText={touched.observaciones && errors.observaciones}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  mt={1}
                  p={2}
                  border={1}
                  borderColor="grey.300"
                  borderRadius={4}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Archivo Adjunto (Certificado / Dropbox)
                  </Typography>

                  {certificado?.nombre_archivo && !archivo && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Archivo actual:{" "}
                      <strong>{certificado.nombre_archivo}</strong>
                    </Typography>
                  )}

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    {archivo
                      ? archivo.name
                      : isEdit
                        ? "Reemplazar Archivo"
                        : "Subir Archivo"}
                    <input
                      type="file"
                      hidden
                      accept=".pfx,.p12,.pdf,.zip"
                      onChange={handleFileChange}
                    />
                  </Button>

                  {archivo && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="primary"
                      style={{ marginTop: 4 }}
                    >
                      Nuevo archivo seleccionado: {archivo.name}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {!isEdit && (
                <Grid item xs={12}>
                  <Box
                    mt={1}
                    p={2}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={4}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Anexos para Tarea Interna (Opcional)
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Sube 0 o más archivos adicionales que quieras adjuntar
                      exclusivamente a la tarea interna generada.
                    </Typography>

                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      size="small"
                    >
                      Seleccionar Anexos
                      <input
                        type="file"
                        hidden
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.zip,.doc,.docx"
                        onChange={handleAnexosChange}
                      />
                    </Button>

                    {anexos.length > 0 && (
                      <Box mt={2}>
                        <Typography variant="caption" color="primary">
                          Anexos seleccionados:
                        </Typography>
                        {anexos.map((file, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            alignItems="center"
                            mt={0.5}
                          >
                            <Typography variant="body2" style={{ flexGrow: 1 }}>
                              {file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveAnexo(idx)}
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogModal>
        </Form>
      )}
    </Formik>
  );
}
